import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Download, ExternalLink, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SearchableSelect from "@/components/SearchableSelect";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";
import { PreviewDialog, previewKind } from "@/components/DocumentsPanel";
import { DOC_TYPES, docTypeLabel } from "@/constants/documentTypes";
import { qboTransactionLink } from "@/lib/qbo";
import { fmtDateTime } from "@/lib/utils";

const RECORD_TYPES = [
  { value: "", label: "Any record" },
  { value: "invoice", label: "Vendor invoices" },
  { value: "outside_purchase", label: "Expense Log" },
  { value: "imported_transaction", label: "Ramp transactions" },
  { value: "vendor_credit", label: "Vendor credits" },
  { value: "purchase_order", label: "Purchase orders" },
  { value: "receiving", label: "Receiving (PO deliveries)" },
];
const RECORD_KIND = Object.fromEntries(RECORD_TYPES.map((r) => [r.value, r.label]));

const recordUrl = (ref) => {
  if (!ref) return null;
  return {
    outside_purchase: `/contractor/expense-log/${ref.id}`,
    invoice: `/contractor/invoices/${ref.id}`,
    purchase_order: `/contractor/purchase-orders/${ref.id}`,
    imported_transaction: "/contractor/card-transactions",
    vendor_credit: "/contractor/accounting?tab=credits",
  }[ref.type];
};

const fmtSize = (n) => (n < 1024 * 1024 ? `${Math.max(1, Math.round((n || 0) / 1024))} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

// Accounting → Documents: every document backing an accounting record, in
// one place, searched on the server — and whether each file is in QuickBooks.
export default function AccountingDocumentsView() {
  const [search, setSearch] = useState("");
  const [docType, setDocType] = useState("");
  const [recordType, setRecordType] = useState("");
  const [inQbo, setInQbo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [environment, setEnvironment] = useState(null);
  const [preview, setPreview] = useState(null);
  const q = useDebounced(search);
  const list = usePagedSearch("/contractor/accounting/documents", {
    q, doc_type: docType, record_type: recordType, in_qbo: inQbo, date_from: dateFrom, date_to: dateTo,
  });
  const counts = list.counts || {};

  useEffect(() => {
    api.get("/contractor/accounting/qbo-environment").then((r) => setEnvironment(r.data.environment)).catch(() => {});
  }, []);

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Failed to download");
    }
  };

  const filterCls = "h-10 px-3 rounded-lg border border-slate-200 text-[13px] min-w-[150px]";
  const inQboTabs = [["", "All", counts.all], ["yes", "In QuickBooks", counts.in_qbo], ["no", "Not in QuickBooks", counts.not_in_qbo]];

  return (
    <div data-testid="accounting-documents">
      <ListSearchBar value={search} onChange={setSearch} placeholder="Search file name, PO #, invoice #, vendor…" testId="documents-search">
        <SearchableSelect value={recordType} onChange={(e) => setRecordType(e.target.value)} className={filterCls} data-testid="documents-filter-record">
          {RECORD_TYPES.map((r) => <option key={r.value || "any"} value={r.value}>{r.label}</option>)}
        </SearchableSelect>
        <SearchableSelect value={docType} onChange={(e) => setDocType(e.target.value)} className={filterCls} data-testid="documents-filter-type">
          <option value="">Any document type</option>
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </SearchableSelect>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Uploaded from" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Uploaded to" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
      </ListSearchBar>

      <div className="flex gap-2 mt-4 flex-wrap">
        {inQboTabs.map(([v, label, n]) => (
          <button key={v || "all"} onClick={() => setInQbo(v)} data-testid={`documents-qbo-${v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium ${inQbo === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
            {label}{n ? ` (${n.toLocaleString()})` : ""}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-[13px]" data-testid="documents-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">File</th>
              <th className="text-left py-2">Type</th>
              <th className="text-left py-2">Record</th>
              <th className="text-left py-2">Vendor</th>
              <th className="text-left py-2">Uploaded</th>
              <th className="text-left py-2">QuickBooks</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(list.items || []).map((d) => {
              const url = recordUrl(d.record_ref);
              const qboLink = d.qbo_record ? qboTransactionLink(environment, d.qbo_record.qbo_type, d.qbo_record.qbo_id) : null;
              const Icon = d.content_type?.startsWith("image/") ? ImageIcon : FileText;
              return (
                <tr key={d.id} className="border-t border-slate-100" data-testid={`document-row-${d.id}`}>
                  <td className="py-2.5 max-w-[260px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon size={14} className="text-slate-400 shrink-0" />
                      <span className="truncate font-medium" title={d.filename}>{d.filename}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 pl-6">{fmtSize(d.size)}</div>
                  </td>
                  <td className="py-2.5 text-slate-500">{docTypeLabel(d.doc_type)}</td>
                  <td className="py-2.5">
                    {url ? <Link to={url} className="mono text-sky-600 font-medium hover:underline">{d.record_label || "Open"}</Link> : <span className="mono">{d.record_label || "—"}</span>}
                    <div className="text-[11px] text-slate-400">{RECORD_KIND[d.entity_type] || d.entity_type}</div>
                  </td>
                  <td className="py-2.5 text-slate-600">{d.vendor_name || "—"}</td>
                  <td className="py-2.5 text-slate-500 whitespace-nowrap">
                    {d.created_at ? fmtDateTime(d.created_at) : "—"}
                    <div className="text-[11px] text-slate-400">{d.uploaded_by_name}</div>
                  </td>
                  <td className="py-2.5 text-[12.5px]">
                    {d.qbo_attached_at ? (
                      qboLink ? <a href={qboLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-700 font-semibold hover:underline">Attached <ExternalLink size={11} /></a>
                        : <span className="text-emerald-700 font-semibold">Attached</span>
                    ) : d.qbo_record ? (
                      <span className="text-amber-700" title="The record is in QuickBooks but this file didn't attach — it's retried the next time the record syncs or a file is added.">Record synced, file not attached</span>
                    ) : (
                      <span className="text-slate-400">Not synced yet</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {previewKind(d) && (
                      <button onClick={() => setPreview(d)} title="Preview" data-testid={`preview-doc-${d.id}`} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 mr-1.5"><Eye size={13} /></button>
                    )}
                    <button onClick={() => download(d)} title="Download" data-testid={`download-doc-${d.id}`} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Download size={13} /></button>
                  </td>
                </tr>
              );
            })}
            {list.items && list.items.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">{q ? "No documents match." : "No accounting documents yet."}</td></tr>
            )}
            {!list.items && <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
      <LoadMore items={list.items} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore}
        total={inQbo === "yes" ? counts.in_qbo : inQbo === "no" ? counts.not_in_qbo : counts.all} />

      <PreviewDialog doc={preview} onClose={() => setPreview(null)} onDownload={download} />
    </div>
  );
}
